import { format, fromUnixTime } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';

const DATE_FNS_LOCALES = { en: enUS, en_US: enUS, pt: ptBR, pt_BR: ptBR };

export const dateFnsLocale = locale =>
  DATE_FNS_LOCALES[locale] ||
  DATE_FNS_LOCALES[locale?.replace('-', '_')] ||
  enUS;

export const formatReportDate = (value, locale, pattern = 'dd MMM') => {
  const date = value instanceof Date ? value : fromUnixTime(Number(value));
  return format(date, pattern, { locale: dateFnsLocale(locale) });
};

export const formatReportDateRange = (startDate, endDate, locale) => {
  const loc = dateFnsLocale(locale);
  if (startDate.getFullYear() !== endDate.getFullYear()) {
    return `${format(startDate, 'd MMM yyyy', { locale: loc })} - ${format(
      endDate,
      'd MMM yyyy',
      { locale: loc }
    )}`;
  }

  return `${format(startDate, 'd MMM', { locale: loc })} - ${format(
    endDate,
    'd MMM yyyy',
    { locale: loc }
  )}`;
};

export const formatLocalizedDuration = (seconds, t) => {
  const total = Math.round(Number(seconds) || 0);
  if (total <= 0) return t('REPORT.DURATION.NONE');

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remaining = total % 60;

  if (days > 0 && hours > 0) {
    if (minutes > 0) {
      return `${t('REPORT.DURATION.DAYS', { count: days })} ${t(
        'REPORT.DURATION.HOURS_MINUTES',
        { hours, minutes }
      )}`;
    }
    return t('REPORT.DURATION.DAYS_HOURS', { days, hours });
  }
  if (days > 0 && minutes > 0) {
    return `${t('REPORT.DURATION.DAYS', { count: days })} ${t(
      'REPORT.DURATION.MINUTES',
      { count: minutes }
    )}`;
  }
  if (days > 0) return t('REPORT.DURATION.DAYS', { count: days });
  if (hours > 0 && minutes > 0) {
    return t('REPORT.DURATION.HOURS_MINUTES', { hours, minutes });
  }
  if (hours > 0) return t('REPORT.DURATION.HOURS', { count: hours });
  if (minutes > 0 && remaining > 0) {
    return t('REPORT.DURATION.MINUTES_SECONDS', {
      minutes,
      seconds: remaining,
    });
  }
  if (minutes > 0) return t('REPORT.DURATION.MINUTES', { count: minutes });

  return t('REPORT.DURATION.SECONDS', { count: remaining });
};

export const seriesSampleSize = points =>
  (points || []).reduce((sum, point) => sum + Number(point.count || 0), 0);

export const hasDurationOutlier = points => {
  const values = (points || [])
    .map(point => Number(point.value) || 0)
    .filter(value => value > 0)
    .sort((first, second) => first - second);
  if (values.length < 3) return false;

  const mid = Math.floor(values.length / 2);
  const median =
    values.length % 2 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
  if (median <= 0) return false;

  return values[values.length - 1] > median * 3;
};

export const isIncompleteLastBucket = (to, groupBy) => {
  if (groupBy !== 'day' || !to) return false;

  const end = fromUnixTime(Number(to));
  const now = new Date();
  return (
    end.getFullYear() === now.getFullYear() &&
    end.getMonth() === now.getMonth() &&
    end.getDate() === now.getDate()
  );
};
